/**
 * Product Type Dispatch Time Manager
 * Calculates dispatch dates based on product type delivery times and business days
 */

// Check if ProductTypeDispatchManager already exists to prevent duplicate declarations
if (typeof window.ProductTypeDispatchManager === 'undefined') {
  class ProductTypeDispatchManager {
    constructor() {
      // Product type dispatch times (business days to get to you)
    this.productTypeDispatchTimes = {
      'Default': { min: 6, max: 12 },
      'Alternative Brewing': { min: 2, max: 5 },
      'Amydor': { min: 7, max: 11 },
      'Barista Group Co': { min: 1, max: 3 },
      'Bezzera': { min: 2, max: 4 },
      'Bombora': { min: 1, max: 2 },
      'Breville': { min: 3, max: 7 },
      'Chai Spice': { min: 4, max: 6 },
      'Coast 2 Coast Distributors': { min: 2, max: 5 },
      'Coffee Tools': { min: 1, max: 3 },
      'Decent Espresso': { min: 4, max: 6 },
      'Dolo Cafe Supplies': { min: 2, max: 5 },
      'Kinu': { min: 6, max: 10 },
      'Loveramics': { min: 4, max: 6 },
      'Made by Fressko': { min: 3, max: 6 },
      'Moccamaster': { min: 3, max: 6 },
      'Phaser FPV': { min: 1, max: 2 },
      'Sheldon and Hammond Pty. Ltd': { min: 3, max: 7 },     
      'Roest': { min: 13, max: 16},
      'Third Wave Water': { min: 5, max: 7},
      
    };
    
    this.defaultMessage = "Available in 4-10 days - Email us to be certain if you want to check";
  }

  /**
   * Check if a date is a weekend
   */
  isWeekend(date) {
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday or Saturday
  }

  /**
   * Add business days to a date, skipping weekends
   */
  addBusinessDays(startDate, businessDays) {
    const date = new Date(startDate);
    let daysAdded = 0;
    
    while (daysAdded < businessDays) {
      date.setDate(date.getDate() + 1);
      if (!this.isWeekend(date)) {
        daysAdded++;
      }
    }
    
    return date;
  }

  /**
   * Get day with ordinal suffix (1st, 2nd, 3rd, etc.)
   */
  getDayWithOrdinal(day) {
    if (day >= 11 && day <= 13) {
      return day + 'th';
    }
    switch (day % 10) {
      case 1: return day + 'st';
      case 2: return day + 'nd';
      case 3: return day + 'rd';
      default: return day + 'th';
    }
  }

  /**
   * Format date to readable string
   */
  formatDate(date) {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  }

  /**
   * Get cart item availability message for a product type (used in cart badges)
   */
  getCartItemAvailabilityMessage(productType, orderDate = new Date()) {
    const dispatchInfo = this.getProductTypeDispatchRange(productType, orderDate);
    
    // Convert "Dispatches around" to "Available around" and add "after ordering" for cart context
    return dispatchInfo.message.replace('Dispatches around ', 'Available around ').concat(' after ordering');
  }

  /**
   * Get pickup availability message for a product type
   */
  getPickupAvailabilityMessage(productType, orderDate = new Date()) {
    const dispatchInfo = this.getProductTypeDispatchRange(productType, orderDate);
    
    // Convert "Dispatches around" to "Pickup around" and add "after ordering"
    return dispatchInfo.message.replace('Dispatches around ', 'Pickup around ').concat(' after ordering');
  }

  /**
   * Get dispatch range for a product type
   */
  getProductTypeDispatchRange(productType, orderDate = new Date()) {
    // Clean product type and check if exists
    const cleanProductType = productType.trim();
    let productTypeConfig = this.productTypeDispatchTimes[cleanProductType];
    
    // If product type not found, use default timing but still calculate actual dates
    if (!productTypeConfig) {
      productTypeConfig = this.productTypeDispatchTimes['Default'];
    }

    // Calculate dispatch dates based on current date/time
    const now = new Date();
    const minDispatchDate = this.addBusinessDays(now, productTypeConfig.min);
    const maxDispatchDate = this.addBusinessDays(now, productTypeConfig.max);

    // Format the message with calculated dates
    let message;
    const isDefault = !this.productTypeDispatchTimes[cleanProductType];
    
    if (productTypeConfig.min === productTypeConfig.max) {
      const dayWithOrdinal = this.getDayWithOrdinal(minDispatchDate.getDate());
      const month = minDispatchDate.toLocaleDateString('en-US', { month: 'long' });
      message = `Dispatches around ${dayWithOrdinal} ${month}`;
    } else {
      const minDay = minDispatchDate.getDate();
      const maxDayWithOrdinal = this.getDayWithOrdinal(maxDispatchDate.getDate());
      // Check if same month
      if (minDispatchDate.getMonth() === maxDispatchDate.getMonth()) {
        const month = minDispatchDate.toLocaleDateString('en-US', { month: 'long' });
        message = `Dispatches around ${minDay}-${maxDayWithOrdinal} ${month}`;
      } else {
        const minMonth = minDispatchDate.toLocaleDateString('en-US', { month: 'long' });
        const maxMonth = maxDispatchDate.toLocaleDateString('en-US', { month: 'long' });
        message = `Dispatches around ${minDay} ${minMonth}-${maxDayWithOrdinal} ${maxMonth}`;
      }
    }

    return {
      message,
      isDefault,
      minDate: minDispatchDate,
      maxDate: maxDispatchDate,
      productType: cleanProductType,
      businessDays: productTypeConfig
    };
  }

  /**
   * Get week day range string (e.g., "Tuesday/Wednesday")
   */
  getWeekString(date) {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  }

  /**
   * Check if cart has any preorder items
   */
  async checkCartForPreorders() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const preorderItems = cart.items.filter(item => {
        return item.product && item.product.type && 
               this.productTypeDispatchTimes[item.product.type.trim()] &&
               item.variant.inventory_quantity <= 0 &&
               item.variant.inventory_policy === 'continue';
      });

      return {
        hasPreorders: preorderItems.length > 0,
        preorderItems,
        dispatchInfo: preorderItems.map(item => ({
          ...item,
          dispatchInfo: this.getProductTypeDispatchRange(item.product.type)
        }))
      };
    } catch (error) {
      return { hasPreorders: false, preorderItems: [], dispatchInfo: [] };
    }
  }

  /**
   * Update product page inventory message
   */
  updateProductInventoryMessage(productType, targetSelector = '.product__inventory') {
    const inventoryElement = document.querySelector(targetSelector);
    if (!inventoryElement) return;

    // Try to get the current variant (from window.product or DOM)
    let variant = null;
    if (window.product && window.product.variants) {
      const selectedId = window.product.selected_or_first_available_variant_id || window.product.selected_variant_id;
      variant = window.product.variants.find(v => v.id == selectedId);
    }
    if (!variant) {
      // Try to get from DOM
      const variantInput = document.querySelector('input[name="id"]:checked') || document.querySelector('select[name="id"]') || document.querySelector('input[name="id"][type="hidden"]');
      if (variantInput && window.product && window.product.variants) {
        variant = window.product.variants.find(v => v.id == parseInt(variantInput.value));
      }
    }

    // Only show dispatch message if out of stock and allows overselling
    if (variant && variant.inventory_quantity <= 0 && variant.inventory_policy === 'continue') {
      const dispatchInfo = this.getProductTypeDispatchRange(productType);
      inventoryElement.textContent = dispatchInfo.message;
    } else {
      // Do not overwrite the in-stock message; leave as is
    }
  }

  /**
   * Initialize product type dispatch checking for product pages
   */
  initProductPage() {
    // Helper to get current product type from product data
    const getCurrentProductType = () => {
      if (window.product && window.product.type) {
        return window.product.type;
      }
      const productData = document.querySelector('[data-product-type]');
      if (productData && productData.dataset.productType) {
        return productData.dataset.productType;
      }
      return null;
    };

    // Initial update
    const updateInventory = () => {
      const productType = getCurrentProductType();
      if (productType) {
        this.updateProductInventoryMessage(productType);
      }
    };

    // Aggressive observer: watch the parent node for inventory element being replaced
    const inventorySelector = '.product__inventory';
    const observeTarget = document.querySelector(inventorySelector)?.parentNode || document.body;
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(() => {
        // Always try to update inventory message if the element exists
        updateInventory();
      });
      observer.observe(observeTarget, { childList: true, subtree: true });
    }

    // Listen for Shopify variant change events
    document.addEventListener('variant:change', (event) => {
      // Get product type from product data
      const productType = getCurrentProductType();
      if (productType) {
        this.updateProductInventoryMessage(productType);
      }
    });

    // Fallback: update on DOMContentLoaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', updateInventory);
    } else {
      updateInventory();
    }
  }

  /**
   * Get unified cart message based on coffee roasting and preorder items
   */
  getUnifiedCartMessage(hasCoffee, hasPreorders, preorderProductTypes = [], roastDate = null, dateOverride = null) {
    const now = new Date();
    let readyMessage = '';
    let contextIcons = '';
    let explanation = '';
    
    // If we have a date override (from actual DOM preorder badges), use it directly
    if (dateOverride && hasPreorders) {
      if (hasCoffee) {
        contextIcons = '📦☕';
        explanation = 'after coffee roasting and restocking';
      } else {
        contextIcons = '📦';
        explanation = 'after restocking';
      }
      
      readyMessage = dateOverride;
      
      return {
        contextIcons,
        readyMessage,
        explanation
      };
    }

    // Calculate preorder date ranges if we have them
    let earliestPreorderDate = null;
    let latestPreorderDate = null;
    
    if (hasPreorders && preorderProductTypes.length > 0) {
      const dispatchInfos = preorderProductTypes.map(productType => 
        this.getProductTypeDispatchRange(productType.trim())
      );
      
      // Include both default and non-default infos to show actual dates
      dispatchInfos.forEach(info => {
        if (info.minDate && info.maxDate) {
          // For earliest date, we need the LATEST minimum date (when everything is ready)
          if (!earliestPreorderDate || info.minDate > earliestPreorderDate) {
            earliestPreorderDate = info.minDate;
          }
          // For latest date, we need the LATEST maximum date
          if (!latestPreorderDate || info.maxDate > latestPreorderDate) {
            latestPreorderDate = info.maxDate;
          }
        }
      });
    }

    // Calculate coffee date range (roast date + 0-1 business days)
    let earliestCoffeeDate = null;
    let latestCoffeeDate = null;
    if (hasCoffee && roastDate) {
      earliestCoffeeDate = new Date(roastDate); // Available same day as roast
      latestCoffeeDate = this.addBusinessDays(roastDate, 1); // Add 1 business day after roast
    }

    // Determine the overall date range and create unified message
    if (hasCoffee && hasPreorders) {
      contextIcons = '📦☕';
      explanation = 'after coffee roasting and restocking';
      
      // Find the overall earliest and latest dates
      let overallEarliest = null;
      let overallLatest = null;
      
      // For earliest, take the LATER of coffee vs preorder earliest dates
      if (earliestCoffeeDate && earliestPreorderDate) {
        overallEarliest = earliestCoffeeDate > earliestPreorderDate ? earliestCoffeeDate : earliestPreorderDate;
      } else if (earliestCoffeeDate) {
        overallEarliest = earliestCoffeeDate;
      } else if (earliestPreorderDate) {
        overallEarliest = earliestPreorderDate;
      }
      
      // For latest, take the LATER of coffee vs preorder latest dates
      if (latestCoffeeDate && latestPreorderDate) {
        overallLatest = latestCoffeeDate > latestPreorderDate ? latestCoffeeDate : latestPreorderDate;
      } else if (latestCoffeeDate) {
        overallLatest = latestCoffeeDate;
      } else if (latestPreorderDate) {
        overallLatest = latestPreorderDate;
      }
      
      if (overallEarliest && overallLatest) {
        const earliestDay = overallEarliest.getDate();
        const latestDay = this.getDayWithOrdinal(overallLatest.getDate());
        const earliestMonth = overallEarliest.toLocaleDateString('en-US', { month: 'long' });
        const latestMonth = overallLatest.toLocaleDateString('en-US', { month: 'long' });
        
        if (earliestMonth === latestMonth) {
          if (earliestDay === overallLatest.getDate()) {
            readyMessage = `around ${latestDay} ${earliestMonth}`;
          } else {
            readyMessage = `around ${earliestDay}-${latestDay} ${earliestMonth}`;
          }
        } else {
          readyMessage = `around ${earliestDay} ${earliestMonth}-${latestDay} ${latestMonth}`;
        }
      } else {
        readyMessage = 'when everything is ready';
      }
      
    } else if (hasCoffee) {
      contextIcons = '☕';
      explanation = 'after coffee roasting';
      
      if (earliestCoffeeDate && latestCoffeeDate) {
        const earliestDay = earliestCoffeeDate.getDate();
        const latestDay = this.getDayWithOrdinal(latestCoffeeDate.getDate());
        const earliestMonth = earliestCoffeeDate.toLocaleDateString('en-US', { month: 'long' });
        const latestMonth = latestCoffeeDate.toLocaleDateString('en-US', { month: 'long' });
        
        if (earliestMonth === latestMonth) {
          if (earliestDay === latestCoffeeDate.getDate()) {
            readyMessage = `around ${latestDay} ${earliestMonth}`;
          } else {
            readyMessage = `around ${earliestDay}-${latestDay} ${earliestMonth}`;
          }
        } else {
          readyMessage = `around ${earliestDay} ${earliestMonth}-${latestDay} ${latestMonth}`;
        }
      } else {
        readyMessage = 'when coffee is roasted';
      }
      
    } else if (hasPreorders) {
      contextIcons = '📦';
      explanation = 'after restocking';
      
      if (earliestPreorderDate && latestPreorderDate) {
        const earliestDay = earliestPreorderDate.getDate();
        const latestDay = this.getDayWithOrdinal(latestPreorderDate.getDate());
        const earliestMonth = earliestPreorderDate.toLocaleDateString('en-US', { month: 'long' });
        const latestMonth = latestPreorderDate.toLocaleDateString('en-US', { month: 'long' });
        
        if (earliestMonth === latestMonth) {
          if (earliestDay === latestPreorderDate.getDate()) {
            readyMessage = `around ${latestDay} ${earliestMonth}`;
          } else {
            readyMessage = `around ${earliestDay}-${latestDay} ${earliestMonth}`;
          }
        } else {
          readyMessage = `around ${earliestDay} ${earliestMonth}-${latestDay} ${latestMonth}`;
        }
      } else {
        readyMessage = 'when items are restocked';
      }
    }

    return {
      hasDelayedItems: hasCoffee || hasPreorders,
      contextIcons,
      readyMessage,
      explanation,
      latestReadyDate: (hasCoffee && hasPreorders) ? 
        (latestCoffeeDate && latestPreorderDate ? (latestCoffeeDate > latestPreorderDate ? latestCoffeeDate : latestPreorderDate) : (latestCoffeeDate || latestPreorderDate)) :
        (hasCoffee ? latestCoffeeDate : latestPreorderDate),
      components: {
        hasCoffee,
        hasPreorders,
        earliestCoffeeDate,
        latestCoffeeDate,
        earliestPreorderDate,
        latestPreorderDate
      }
    };
  }

  /**
   * Get list of available product types (for admin reference)
   */
  getAvailableProductTypes() {
    return Object.keys(this.productTypeDispatchTimes);
  }
}

  // Initialize the manager
  window.productTypeDispatchManager = new ProductTypeDispatchManager();
} else {
  // ProductTypeDispatchManager already exists, skipping initialization
}

// Auto-initialize on product pages
document.addEventListener('DOMContentLoaded', function() {
  if (window.productTypeDispatchManager) {
    window.productTypeDispatchManager.initProductPage();
  }
});

// Add refresh method for cart usage
if (window.productTypeDispatchManager) {
  window.productTypeDispatchManager.refreshCartDates = function() {
    // This can be called to force refresh all dispatch dates
    const cartItemElements = document.querySelectorAll('[class*="cart-item-preorder-dispatch"]');
    
    cartItemElements.forEach(element => {
      const productType = element.dataset.productType;
      
      if (productType) {
        const cartMessage = this.getCartItemAvailabilityMessage(productType);
        let message = cartMessage.replace(' after ordering', '');
        if (message.startsWith('Available around ')) {
          message = message.replace('Available around ', 'around ');
        }
        element.textContent = message;
        element.setAttribute('data-dispatch-updated', 'true');
      }
    });
    
    // Also call the cart item's own update function if it exists
    if (window.updateCartPreorderDates) {
      window.updateCartPreorderDates();
    }
  };
  
  /**
   * Update unified cart banner based on current cart contents
   */
  window.productTypeDispatchManager.updateUnifiedCartBanner = function() {
    const unifiedBanner = document.querySelector('.cart-unified-notice');
    if (!unifiedBanner) {
      return Promise.resolve();
    }

    return fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        
        // Check if we have coffee items (by variant title)
        const hasUpcomingRoast = cart.items.some(item => 
          item.variant_title && item.variant_title.includes('Upcoming Roast')
        );
        
        // Use DOM-based preorder detection from banner manager
        let hasPreorderItems = false;
        let preorderProductTypes = [];
        
        if (window.cartPagePreorderFlags && window.cartPagePreorderFlags.preorderItemsDetected) {
          hasPreorderItems = true;
          
          // Instead of getting product types from cart API, scan the DOM for actual preorder badges
          // and extract their dates to find the latest one
          const preorderBadges = document.querySelectorAll('.cart-item__preorder-badge .preorder-dispatch-text');
          const preorderDates = [];
          
          preorderBadges.forEach(badge => {
            const dateText = badge.textContent;
            const productType = badge.dataset.productType;
            if (productType) {
              preorderProductTypes.push(productType);
            }
            
            // Extract date range from text like "around 17-23rd September" or "around 1-6th October"
            const dateMatch = dateText.match(/around\s+(\d+)[-–](\d+)\w*\s+(\w+)/);
            if (dateMatch) {
              const [, startDay, endDay, month] = dateMatch;
              // Create a date object for comparison (using end day as it's the latest)
              const currentYear = new Date().getFullYear();
              const monthIndex = ['January', 'February', 'March', 'April', 'May', 'June', 
                                'July', 'August', 'September', 'October', 'November', 'December']
                                .indexOf(month);
              if (monthIndex !== -1) {
                const dateObj = new Date(currentYear, monthIndex, parseInt(endDay));
                preorderDates.push({
                  date: dateObj,
                  text: dateText,
                  productType
                });
              }
            }
          });
          
          // Sort by date to find the latest
          preorderDates.sort((a, b) => b.date - a.date);
          
          // Store the latest preorder info for the banner calculation
          if (preorderDates.length > 0) {
            window.latestPreorderInfo = preorderDates[0];
          }
          
        } else {
          // Fallback to checking DOM for preorder badges
          hasPreorderItems = document.querySelector('.cart-item__preorder-badge') !== null;
        }
        
        const shouldShow = hasUpcomingRoast || hasPreorderItems;
        
        unifiedBanner.style.display = shouldShow ? 'block' : 'none';
        
        if (shouldShow) {
          // Use stored roast date if available
          const roastDate = window.latestRoastDate || null;
          
          // For mixed orders with preorders, use the latest preorder date as override
          let dateOverride = null;
          if (hasPreorderItems && window.latestPreorderInfo) {
            // Extract just the date range from the preorder text
            const preorderDateText = window.latestPreorderInfo.text;
            if (preorderDateText.includes('around ')) {
              dateOverride = preorderDateText.replace('around ', '');
            }
          }
          
          const unifiedMessage = this.getUnifiedCartMessage(
            hasUpcomingRoast, 
            hasPreorderItems, 
            preorderProductTypes,
            roastDate,
            dateOverride  // Pass the latest preorder date as override
          );

          const iconsElement = document.getElementById('cartUnifiedIcons');
          const readyDateElement = document.getElementById('cartUnifiedReadyDate');
          const explanationElement = document.getElementById('cartUnifiedExplanation');

          if (iconsElement && readyDateElement && explanationElement) {
            iconsElement.textContent = unifiedMessage.contextIcons;
            readyDateElement.textContent = unifiedMessage.readyMessage;
            explanationElement.textContent = unifiedMessage.explanation;
          }

          // If we have coffee but no stored roast date, fetch it
          if (hasUpcomingRoast && !window.latestRoastDate) {
            return this.fetchAndUpdateRoastDate().then(roastDate => {
              if (roastDate) {
                // Recalculate with actual roast date
                const updatedMessage = this.getUnifiedCartMessage(
                  hasUpcomingRoast, 
                  hasPreorderItems, 
                  preorderProductTypes,
                  roastDate
                );

                if (iconsElement && readyDateElement && explanationElement) {
                  iconsElement.textContent = updatedMessage.contextIcons;
                  readyDateElement.textContent = updatedMessage.readyMessage;
                  explanationElement.textContent = updatedMessage.explanation;
                }
              }
            });
          }
        }
        
        return Promise.resolve();
      })
      .catch(error => {
        return Promise.resolve();
      });
  };

  /**
   * Fetch roast date from Google Calendar and update cart items
   */
  window.productTypeDispatchManager.fetchAndUpdateRoastDate = function() {
    const apiKey = 'AIzaSyA-bmNJ6umqEby4a5KMSxIqdJESidLkEY0';
    const calendarId = encodeURIComponent('ccoastcoffee@gmail.com');
    
    const now = new Date();
    const sydneyTime = new Date(new Intl.DateTimeFormat('en-US', {
      timeZone: 'Australia/Sydney',
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', second: 'numeric',
      hour12: false
    }).format(now));
    
    const todayCutoff = new Date(sydneyTime);
    todayCutoff.setHours(7, 0, 0, 0);
    
    const todayStart = new Date(sydneyTime);
    todayStart.setHours(0, 0, 0, 0);

    const url = `https://www.googleapis.com/calendar/v3/calendars/${calendarId}/events?orderBy=startTime&singleEvents=true&timeMin=${todayStart.toISOString()}&maxResults=10&key=${apiKey}`;

    return fetch(url)
      .then(response => response.json())
      .then(data => {
        if (data.items && data.items.length > 0) {
          let nextEvent;
          
          if (sydneyTime < todayCutoff) {
            nextEvent = data.items.find(event => {
              const eventDate = new Date(event.start.date || event.start.dateTime);
              const eventDateSydney = new Date(new Intl.DateTimeFormat('en-US', {
                timeZone: 'Australia/Sydney',
                year: 'numeric', month: 'numeric', day: 'numeric',
                hour: 'numeric', minute: 'numeric', second: 'numeric'
              }).format(eventDate));
              
              return eventDateSydney.toDateString() === sydneyTime.toDateString();
            }) || data.items.find(event => {
              const eventDate = new Date(event.start.date || event.start.dateTime);
              return eventDate > now;
            });
          } else {
            nextEvent = data.items.find(event => {
              const eventDate = new Date(event.start.date || event.start.dateTime);
              return eventDate > now;
            });
          }

          if (nextEvent) {
            const roastDate = new Date(nextEvent.start.date || nextEvent.start.dateTime);
            
            const formattedRoastDate = roastDate.toLocaleDateString('en-US', {
              weekday: 'long', day: 'numeric', month: 'long'
            }).replace(/(\w+)/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());

            // Update all cart item roast dates
            const cartItemDates = document.querySelectorAll('[class*="cart-item-roast-date"]');
            cartItemDates.forEach(element => {
              element.textContent = formattedRoastDate;
              element.setAttribute('data-roast-date', 'updated');
            });
            
            // Store the roast date for unified banner calculation
            window.latestRoastDate = roastDate;
            
            return roastDate;
          }
        }
        return null;
      })
      .catch(error => {
        return null;
      });
  };
}

// Backward compatibility aliases
window.vendorDispatchManager = window.productTypeDispatchManager;
